import { useEffect, useRef, useState } from "react";
import { ArrowLeft, Building2, Bus, Users, ClipboardList, ShieldAlert, FileCheck, Lock, CloudUpload } from "lucide-react";
import { saveRecord, formatDatum } from "../lib/storage";
import OsnovniTab from "./tabs/OsnovniTab";
import AutobusiTab from "./tabs/AutobusiTab";
import PosjetiteljiTab from "./tabs/PosjetiteljiTab";
import ZapazanjaTab from "./tabs/ZapazanjaTab";
import SigurnostTab from "./tabs/SigurnostTab";
import PregledTab from "./tabs/PregledTab";

const allTabs = [
  { id: "osnovni", label: "Osnovni podaci", icon: Building2, testId: "tab-osnovni-podaci" },
  { id: "autobusi", label: "Autobusi", icon: Bus, testId: "tab-autobusi", nastavniOnly: true, opcija: "autobusi" },
  { id: "posjetitelji", label: "Evidencija posjetitelja", icon: Users, testId: "tab-posjetitelji" },
  { id: "zapazanja", label: "Zapažanja tijekom dežuranja", icon: ClipboardList, testId: "tab-zapazanja" },
  { id: "sigurnost", label: "Posebni sigurnosni događaji", icon: ShieldAlert, testId: "tab-sigurnost" },
  { id: "pregled", label: "Pregled i zaključavanje", icon: FileCheck, testId: "tab-pregled-zakljucavanje" },
];

export default function Editor({ initialRecord, settings, onBack }) {
  const [record, setRecord] = useState(initialRecord);
  const [activeTab, setActiveTab] = useState("osnovni");
  const [saved, setSaved] = useState(true);
  const timer = useRef(null);

  const tabs = allTabs.filter((t) => {
    if (t.nastavniOnly && record.tip !== "nastavni") return false;
    if (t.opcija && !settings.opcije[t.opcija]) return false;
    return true;
  });

  const update = (patch) => {
    const next = { ...record, ...patch };
    setRecord(next);
    setSaved(false);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      saveRecord(next);
      setSaved(true);
    }, 400);
  };

  useEffect(() => () => clearTimeout(timer.current), []);

  const locked = record.pregled.zakljucano;

  const renderTab = () => {
    switch (activeTab) {
      case "osnovni": return <OsnovniTab record={record} update={update} settings={settings} />;
      case "autobusi": return <AutobusiTab record={record} update={update} />;
      case "posjetitelji": return <PosjetiteljiTab record={record} update={update} />;
      case "zapazanja": return <ZapazanjaTab record={record} update={update} />;
      case "sigurnost": return <SigurnostTab record={record} update={update} />;
      case "pregled": return <PregledTab record={record} update={update} settings={settings} />;
      default: return null;
    }
  };

  return (
    <div className="animate-fade-in" data-testid="editor-view">
      <div className="mb-6 flex flex-col gap-4 border-b border-slate-200 pb-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <button
            data-testid="editor-natrag-btn"
            onClick={onBack}
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-slate-600 shadow-sm transition-colors hover:bg-slate-50"
          >
            <ArrowLeft className="h-4 w-4" /> Početni ekran
          </button>
          <div>
            <h1 className="font-heading text-lg font-bold text-slate-900 sm:text-xl">
              {record.tip === "nastavni" ? "Evidencija za nastavni dan" : "Evidencija za nenastavni dan"}
            </h1>
            <p className="text-xs text-slate-500">{formatDatum(record.datum)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {record.primjer && (
            <span className="inline-flex items-center rounded-full border border-violet-200 bg-violet-50 px-3 py-1.5 text-xs font-semibold text-violet-700" data-testid="editor-primjer-badge">
              Ogledni primjer
            </span>
          )}
          {locked && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700" data-testid="editor-locked-badge">
              <Lock className="h-3.5 w-3.5" /> Zaključano
            </span>
          )}
          <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ${saved ? "border border-emerald-200 bg-emerald-50 text-emerald-700" : "border border-slate-200 bg-slate-50 text-slate-500"}`} data-testid="editor-autosave-badge">
            <CloudUpload className="h-3.5 w-3.5" /> {saved ? "Spremljeno lokalno" : "Spremanje..."}
          </span>
        </div>
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-1.5 border-b border-slate-200 pb-2">
        {tabs.map((t, i) => {
          const Icon = t.icon;
          const active = activeTab === t.id;
          return (
            <button
              key={t.id}
              data-testid={t.testId}
              onClick={() => setActiveTab(t.id)}
              className={`inline-flex shrink-0 items-center gap-2 rounded-lg px-3.5 py-2.5 text-xs font-semibold whitespace-nowrap transition-all sm:text-sm ${
                active ? "bg-sky-600 text-white shadow-sm" : "text-slate-600 hover:bg-sky-50 hover:text-sky-700"
              }`}
            >
              <Icon className="h-4 w-4" />
              <span>{i + 1}. {t.label}</span>
            </button>
          );
        })}
      </div>

      {locked && activeTab !== "pregled" ? (
        <fieldset disabled className="opacity-70">{renderTab()}</fieldset>
      ) : (
        renderTab()
      )}
    </div>
  );
}
